import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout, type User } from '../store/slices/authSlice';
import type { RootState, AppDispatch } from '../store/index';

interface UserContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: User, token: string) => void;
  logout: () => void;
  refreshUser: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize user data from localStorage on app start
  useEffect(() => {
    const initializeUser = () => {
      try {
        const currentUser = localStorage.getItem('currentUser');
        const token = localStorage.getItem('authToken');
        
        if (currentUser && token) {
          // User data is already in Redux state, no need to set it again
        } else {
          // Clear invalid data
          dispatch(logout());
        }
      } catch (error) {
        
        dispatch(logout());
      } finally {
        setIsLoading(false);
      }
    };

    initializeUser();
  }, [dispatch]);

  const login = (userData: User, token: string) => {
    try {
      localStorage.setItem('authToken', token);
      localStorage.setItem('currentUser', JSON.stringify(userData));
      // User state is managed by Redux, no need to set it here
    } catch (error) {
      
      throw error;
    }
  };

  const logoutUser = () => {
    try {
      dispatch(logout());
      localStorage.removeItem('authToken');
      localStorage.removeItem('currentUser');
    } catch (error) {
      
    }
  };

  const refreshUser = () => {
    try {
      const currentUser = localStorage.getItem('currentUser');
      const token = localStorage.getItem('authToken');
      if (currentUser && token) {
        // User state is managed by Redux
      } else {
        logoutUser();
      }
    } catch (error) {
      
      logoutUser();
    }
  };

  const isAuthenticated = !!user && !!localStorage.getItem('authToken');

  const value: UserContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout: logoutUser,
    refreshUser,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
